import Bluebird from 'bluebird';
import CacheG from '../../../../../services/cache';
import { setLocalItem } from '../../../services/browser-storage';
import getExchangeRates from '../../../services/exchange-rates';
import { selectOption } from './currency-view';

export type CurrencyCode = 'EUR' | 'GBP' | 'USD';
export const defaultCurrency: CurrencyCode = 'EUR';

export interface ICurrency {
    code: CurrencyCode; // ISO4217 code
    exchange: number; // Exchange rate where EUR=1
}

const currencyCache = new CacheG<ICurrency>();

let resolveLoading;
const loading = new Bluebird(resolve => { resolveLoading = resolve; });
export const getCurrencyLoading = () => loading;
export const setCurrencyLoaded = () => resolveLoading();

let selectedCurr: CurrencyCode;
export const getSelectedCurrency = () => selectedCurr;

export function setSelectedCurrency(code: CurrencyCode): Bluebird<void> {
    selectedCurr = code;
    return getCurrency(code).then(handleCurrencyChange);
}

export async function trySetSelectedCurrency(code: string): Bluebird<void> {
    if (await isCurrency(code)) {
        setSelectedCurrency(code as CurrencyCode);
    } else {
        throw new Error(`Tried to select unknown currency '${code}'`);
    }
}

export async function isCurrency(code: string): Bluebird<boolean> {
    if (!code) return false;
    return code in await getExchangeRates();
}

export async function getCurrency(code: CurrencyCode): Bluebird<ICurrency> {
    // Check cache
    let result = currencyCache.find(code);
    if (result) return result;

    // Currency not in cache, try to compose one
    const rates = await getExchangeRates();
    const rate = rates[code];

    if (!rate) {
        throw new Error(`No exchange rate found for currency '${code}'`);
    }

    result = {
        code,
        exchange: rates[code]
    };

    currencyCache.set(code, result);
    return result;
}

const subscribers: Array<(c: ICurrency) => void> = [];
export async function onCurrencyChange(handler: (currency: ICurrency) => void,
    executeOnInit?: boolean): Bluebird<void> {

    // Get notified when currency changes
    subscribers.push(handler);

    if (!executeOnInit) return;

    await loading;
    return Bluebird.all([
        getCurrency(getSelectedCurrency()),
        getCurrencyLoading()
    ]).then(([selected]) => handler(selected));
}

export function handleCurrencyChange(currency: ICurrency): void {
    setLocalItem('currency', currency.code);
    selectOption(currency.code);
    subscribers.forEach((notify: (c: ICurrency) => void) => notify(currency));
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/assets/scripts/modules/bundle/currencies/currency-storage.ts